import React, { useEffect, useMemo, useState, useRef } from 'react';
import AnalyticsInterval from '../../../models/analytics/AnalyticsInterval';
import AnalyticsRequestFilter from '../../../models/analytics/AnalyticsRequestFilter';
import SessionAnalyticsContainer, { SessionListModel } from '../../../hooks/SessionAnalyticsContainer';
import styled from '@emotion/styled';
import { Dictionary } from 'lodash';
import SsmlContainer from '../../../state/containers/SsmlContainer';
import UserApplicationPreferencesContainer from '../../../state/containers/UserApplicationPreferencesContainer';
import ContentHitEvent from '../../../models/analytics/api/ContentHitEvent';
import CustomAssistantResponse from '../../../models/customAssistant/CustomAssistantResponse';
import CustomScrollbars from '../../../components/structure/CustomScrollbars';
import useWindowSize from '../../../hooks/UseWindowSize';
import Loader from '../../../components/general/Loader';
import SimulatorConversationHistoryItem from '../../simulator/components/SimulatorConversationHistoryItem';
import IGenericContentContainer from '../../../state/definitions/IGenericContentContainer';
import ContentItemModel from '../../../models/features/api/ContentItemModel';
import SimulatorContainer from '../../../hooks/SimulatorContainer';
import moment from 'moment';
import { color_colors_ocean, color_shades_dark, color_shades_darkest, color_shades_lighter, color_text_light } from '../../../constants/colors';
import { SESSION_DATE_FORMAT } from '../../../models/analytics/AnalyticsDateFormat';
import ConversationTurn from '../../../models/sessionAttributes/ConversationTurn';
import qs from 'query-string';
import SearchField from '../../../components/forms/SearchField';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import CustomAssistantRequest from '../../../models/customAssistant/CustomAssistantRequest';
import SessionReview from './SessionReview';
import CodeOutputModal from '../../simulator/components/CodeOutputModal';
import SparkContainer from '../../../hooks/SparkContainer';
import useIsMobile from '../../../hooks/UseIsMobile';
import CollapsingPanel from '../../../components/structure/Panels/CollapsingPanel';
import CollapsedMenuPanel from '../../../components/structure/CollapsedMenuPanel';
import ExpandedMenuPanel from '../../../components/structure/ExpandedMenuPanel';
import { breakpoint_small } from '../../../constants/breakpoints';
import ContentContainer from '../../../hooks/ContentContainer';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';

interface ApplicationSessionAnalyticsProps {
    interval: AnalyticsInterval
    innerInterval: AnalyticsInterval
    filter: AnalyticsRequestFilter
    applicationId: string,
    ssmlContainer: SsmlContainer
    userApplicationPreferencesContainer: UserApplicationPreferencesContainer
    contentContainers: Dictionary<IGenericContentContainer<ContentItemModel, any, any, any>>
    history: any
    envContainer: ApplicationEnvironmentsContainer,
    from?: string
    application: ApplicationModel
}

const ApplicationSessionAnalytics = (props: ApplicationSessionAnalyticsProps) => {
    const [selectedSession, setSelectedSession] = useState("");
    const [sessionToLoad, setSessionToLoad] = useState([]);
    const [allEvents, setAllEvents] = useState([]);
    const [contentHits, setContentHits] = useState([]);
    const [filteredSessions, setFilteredSessions] = useState([] as SessionListModel[]);
    const [loaded, setLoaded] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [eventsToShow, setEventsToShow] = useState(null as any);
    const [endDate, setEndDate] = useState("");
    const [detailedAnalyticsEnabled, setDetailedAnalyticsEnabled] = useState(true);
    const [searchFieldValue, setSearchFieldValue] = useState('');
    const [isLeftExpanded, setIsLeftExpanded] = useState<boolean>(true);
    const [touchStartY, setTouchStartY] = useState<number>(0);
    const [touchStartTime, setTouchStartTime] = useState<number>(0);
    const [mobileViewMode, setMobileViewMode] = useState<'session' | 'history'>('session');

    const simulatorContainer = SimulatorContainer.useContainer();
    const sessionContainer = SessionAnalyticsContainer.useContainer();
    const sparkContainer = SparkContainer.useContainer();
    const contentContainer = ContentContainer.useContainer();
    const windowSize = useWindowSize();
    const isMobile = useIsMobile();

    const sessionPillRefs = useRef<Record<string, HTMLDivElement | null>>({});
    // Track if the session change was triggered by user selection
    const userSelectedSessionRef = useRef(false);

    // Reset userSelectedSessionRef when navigating to this page (e.g., via React Router Link)
    useEffect(() => {
        userSelectedSessionRef.current = false;
    }, [window.location.pathname]);

    useEffect(() => {
        const query = qs.parse(window.location.search);
        const search = query?.initialSearch as string;
        if (search?.length) {
            setSearchFieldValue(search);
        }

    }, [window.location.search]);

    useEffect(() => {
        const query = qs.parse(window.location.search);
        const selectedId = query?.sessionId as string;
        // Only scroll if the session change was NOT triggered by user selection
        if (!userSelectedSessionRef.current && selectedId) {
            // Wait for the DOM to paint the session pill
            const timeout = setTimeout(() => {
                const pill = sessionPillRefs.current[selectedId];
                if (pill) {
                    pill.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
                }
                userSelectedSessionRef.current = false;
            }, 0);
            return () => clearTimeout(timeout);
        } else {
            userSelectedSessionRef.current = false;
        }
    }, [window.location.search, filteredSessions]);

    useEffect(() => {
        simulatorContainer.startNewSession();
        initialLoad();
    }, [])

    useEffect(() => {
        if (selectedSession?.length
            && selectedSession in sessionContainer.events
            && (!allEvents.length || allEvents[0].sessionId !== selectedSession)) {
            const newEvents = sessionContainer.events[selectedSession];
            // sort by event date
            newEvents.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());
            setAllEvents(newEvents);
        }
    }, [sessionContainer.events, selectedSession])

    useEffect(() => {
        const contentHits = allEvents?.filter(e => e.eventType === "ContentHit");
        // group by req id
        const grouped = contentHits.reduce((acc, hit) => {
            if (!acc[hit.requestId]) {
                acc[hit.requestId] = [];
            }
            acc[hit.requestId].push(hit);
            return acc;
        }, {} as Dictionary<ContentHitEvent[]>);
        // take last hit of each group
        const lastHits = Object.values(grouped).map((hits: []) => hits[hits.length - 1]);
        if (lastHits.length) {
            setSessionToLoad(lastHits);
            setContentHits(lastHits);
        }
    }, [allEvents])

    useEffect(() => {
        if (sessionToLoad.length) {
            loadSessionTurn();
        }
    }, [sessionToLoad])

    useEffect(() => {
        let newFilteredSessions = sessionContainer.sessions;
        const query = qs.parse(window.location.search);
        if (query?.contentId?.length) {
            contentContainer.getSession(props.applicationId, query.contentId as string, props.filter).then(sessionIds => {
                if (sessionIds) {
                    setFilteredSessions(sessionContainer.sessions.filter(s => sessionIds.includes(s.sessionId)));
                }
            });

            return;
        }

        if (searchFieldValue?.length) {
            newFilteredSessions = [];
            for (const session of sessionContainer.sessions) {
                if (session.sessionId.includes(searchFieldValue))
                    newFilteredSessions.push(session);
                else if (session?.phoneNumbers?.some(p => p.includes(searchFieldValue))) {
                    newFilteredSessions.push(session);
                }
            }
        }
        setFilteredSessions(newFilteredSessions);
    }, [sessionContainer.sessions, searchFieldValue])

    useEffect(() => {
        if (props.envContainer.state.currentAppId === props.applicationId
            && props.envContainer.state.environments?.length) {
            setDetailedAnalyticsEnabled(props.envContainer.state.environments?.[0]?.detailedAnalyticsEnabled ?? false);
        }
    }, [props.envContainer.state.environments])

    useEffect(() => {
        if (startDate === "" && props.filter.startDate) {
            setStartDate(props.filter?.startDate);
            setEndDate(props.filter?.endDate);
            return;
        }
        if (props.filter?.startDate !== startDate || props.filter?.endDate !== endDate) {
            setStartDate(props.filter?.startDate);
            setEndDate(props.filter?.endDate);
            simulatorContainer.startNewSession();
            sessionContainer.loadSessions(props.applicationId, props.filter);
        }
    }, [props.filter])

    useEffect(() => {
        const query = qs.parse(window.location.search);
        if (query?.sessionId?.length) {
            if (selectedSession === query.sessionId)
                return;
            if (sessionContainer.sessions.some(s => s.sessionId === query.sessionId as string)) {
                setSelectedSession(query.sessionId as string);
            }
        }
    }, [window.location.search, sessionContainer.sessions])

    useEffect(() => {
        if (selectedSession.length == 0) return;
        sessionContainer.loadEventsForSession(props.applicationId, selectedSession);
        setLoaded(true);
    }, [selectedSession]);

    const convertEventsForCodeOutputModal = (events: any[]) => {
        var returnObj = {};
        events?.forEach((e, idx) => {
            returnObj[`${idx}-${e.eventType}`] = e;
        })

        // get last event that is request received
        const lastContentHit = events?.slice()?.reverse()?.find(e => e.eventType === "ContentHit");
        if (lastContentHit?.originalRequest?.sessionAttributes) {
            returnObj[`${events.length}-LatestSessionAttributes`] = lastContentHit.originalRequest.sessionAttributes;
        }

        return returnObj;
    }

    const memoizedModal = useMemo(
        () => (
            <CodeOutputModal
                title="JSON"
                closeModal={() => setEventsToShow(null)}
                data={convertEventsForCodeOutputModal(eventsToShow)}
                showCodeOutputModal={eventsToShow}
            />
        ),
        [eventsToShow],
    );

    const loadSessionTurn = async () => {
        const hit = sessionToLoad[0];
        const newResponses = [];
        newResponses.push(mapContentHitToResponse(hit))
        // we're adding the hit to the simulator container so that we can use the simulator to render the conversation
        await simulatorContainer.updateConversationHistory({
            id: hit.sessionId,
            sessionAttributes: {
                ...(hit.originalRequest?.sessionAttributes ?? {}),
                ...(hit.response?.additionalSessionAttributes ?? {})
            }
        },
            newResponses,
            props.contentContainers,
            mapContentHitToRequest(hit),
            mapContentHitToConversationTurn(hit)
        )
        setSessionToLoad(sessionToLoad.slice(1));
    }

    const mapContentHitToResponse = (hit: ContentHitEvent): CustomAssistantResponse => {
        let resposneContent = hit.response?.data?.content;
        if (hit?.response?.followUp?.content?.length)
            resposneContent = `${resposneContent} ${hit?.response?.followUp?.content}`;
        if (hit)
            return {
                responseId: hit.response?.data?.responseId,
                ssml: resposneContent,
                outputSpeech: resposneContent,
                displayText: resposneContent,
                displayTitle: resposneContent,
                endSession: false,
                hints: hit.response?.followUp?.followupHints ?? [],
                sessionAttributes: hit.originalRequest?.sessionAttributes ?? {},
                nlp: hit.originalRequest?.nlp,
                responseIdentifier: hit?.response?.data?.name?.length ? hit.response.data.name : hit.response?.data?.responseId?.length ? hit.response?.data?.responseId : null
            }
    }

    const mapContentHitToRequest = (hit: ContentHitEvent): CustomAssistantRequest => {
        let responseContent = hit.response?.data?.content;
        if (hit?.response?.followUp?.content?.length)
            responseContent = `${responseContent} ${hit?.response?.followUp?.content}`;
        if (hit)
            return hit?.originalRequest?.nativeRequest;
        else
            return {
                requestId: hit.requestId,
                context: {
                    sessionId: hit.sessionId,
                    noTracking: false,
                    requestType: hit.originalRequest?.requestType,
                    originalInput: hit.originalRequest?.originalInput,
                    channel: hit.originalRequest?.channel ?? "",
                    requiresLanguageUnderstanding: hit.originalRequest?.nativeRequest?.requiresLanguageUnderstanding ?? true,
                    locale: hit.originalRequest?.locale,
                },
                device: null,
                user: null
            };
    }

    const initialLoad = async () => {
        const envsResult = await props.envContainer.loadEnvironments(props.applicationId);
        if (envsResult.resultType !== 'Ok') {
            setDetailedAnalyticsEnabled(false);
            setLoaded(true);
            return;
        }
        const enabled = envsResult.data[0]?.detailedAnalyticsEnabled ?? false;
        setDetailedAnalyticsEnabled(enabled);
        if (!enabled) {
            setLoaded(true);
            return;
        }
        const query = qs.parse(window.location.search);
        sessionContainer.loadSessions(props.applicationId, props.filter);
        setLoaded(true);
    }

    const mapContentHitToConversationTurn = (hit: ContentHitEvent): ConversationTurn => {
        return {
            ContentId: hit?.response?.contentItem?.contentItemId,
            FeatureTypeId: hit?.featureTypeId,
            ApplicationId: props.applicationId,
            FollowUpId: hit?.response?.followUp?.id,
            HelpMessageId: '',
            FallbackMessageId: '',
            ChildTurns: [],
            IsLimitedToChildren: hit?.response?.followUp?.isLimitedToChildren,
            RequestDate: new Date(hit?.eventDate),
        }
    };

    const updateFilterToBeginningAndEndOfDay = (filter) => {
        const newStartDate = new Date(filter.startDate);
        newStartDate.setUTCHours(0, 0, 0, 0);
        const newEndDate = new Date(filter.endDate);
        newEndDate.setUTCHours(23, 59, 59, 999);
        return {
            ...filter,
            startDate: newStartDate.toISOString(),
            endDate: newEndDate.toISOString()
        }
    };

    const handleToggleExpandLeft = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, expanded: boolean) => {
        setIsLeftExpanded(expanded);
        localStorage.setItem("contentMenu", expanded?.toString());
        e?.stopPropagation();
    };

    const formatPhoneNumber = (phoneNumber: string) => {
        // Remove any non-digit characters from the input number
        const cleanedNumber = phoneNumber?.replace(/\D/g, '');
        const formattedNumber = `${cleanedNumber?.substring(7)}`;
        return formattedNumber;
    };

    const renderViewerWrapper = () => {
        const renderSessionView = () => (
            sessionContainer.loadingEvents ? <Loader /> :
                allEvents?.length ?
                    <SessionReviewContainer>
                        <SessionReview
                            showFrom
                            manualHeight={(windowSize.windowSize.innerHeight) - (isMobile ? 300 : 250)}
                            events={allEvents}
                            applicationId={props.applicationId}
                            hideBorder
                            simple={sessionContainer.simpleView || sparkContainer.inSpark}
                            setEventsToShow={setEventsToShow}
                            resetCopyButtonText={() => null}
                            application={props.application}

                        />
                    </SessionReviewContainer>
                    :
                    <>
                        <EmptyConversationText>
                            <h4>Select a session</h4>
                            <p>to view the conversation</p>
                        </EmptyConversationText>
                        <Loader static />
                    </>
        );

        const renderHistoryView = () => (
            <HistoryViewContainer>
                <CustomScrollbars
                    autoHide
                    autoHeight
                    autoHeightMin={(windowSize.windowSize.innerHeight) - (isMobile ? 300 : 250)}
                    autoHeightMax={(windowSize.windowSize.innerHeight) - (isMobile ? 300 : 250)}
                >
                    {simulatorContainer.contentItemsFromHistory?.length ? <HistoryHeader>{"CONVERSATION HISTORY"}</HistoryHeader> : null}
                    <HistoryList>
                        {simulatorContainer.contentItemsFromHistory.map((c, idx) =>
                            <SimulatorConversationHistoryItem
                                key={idx}
                                turn={mapContentHitToConversationTurn(contentHits[idx])}
                                content={c}
                                simple={sessionContainer.simpleView}
                                index={idx}
                            />
                        )}
                    </HistoryList>
                </CustomScrollbars>
            </HistoryViewContainer>
        );

        return (
            <ViewerWrapper>
                {isMobile && allEvents?.length > 0 && (
                    <MobileViewToggle>
                        <ToggleButton
                            active={mobileViewMode === 'session'}
                            onClick={() => setMobileViewMode('session')}
                        >
                            Session Review
                        </ToggleButton>
                        <ToggleButton
                            active={mobileViewMode === 'history'}
                            onClick={() => setMobileViewMode('history')}
                        >
                            Conversation History
                        </ToggleButton>
                    </MobileViewToggle>
                )}
                {isMobile ? (
                    mobileViewMode === 'session' ? renderSessionView() : renderHistoryView()
                ) : (
                    renderSessionView()
                )}
            </ViewerWrapper>
        )
    };

    const renderSessionList = () => {
        return (
            <SessionListWrapper>
                <CustomScrollbars
                    autoHide
                    autoHeight
                    autoHeightMin={(windowSize.windowSize.innerHeight) - 200}
                    autoHeightMax={(windowSize.windowSize.innerHeight) - 200}>
                    <SessionList>
                        <SessionsHeader>
                            {sparkContainer.inSpark ? <LeftHeader>{`${filteredSessions.length} CALLS`}</LeftHeader> : <LeftHeader>{`${filteredSessions.length} SESSIONS`}</LeftHeader>}
                        </SessionsHeader>
                        <StyledSearchField
                            id="searchField"
                            name={"searchField"}
                            placeholder={"Session or Content Item ID"}
                            value={searchFieldValue}
                            disabled={!loaded}
                            onChange={(e) => setSearchFieldValue(e.target.value)}
                            boxType={"square"}
                        />
                        {
                            filteredSessions.map((session, idx) => {
                                return (
                                    <SessionPill
                                        ref={el => { sessionPillRefs.current[session.sessionId] = el; }}
                                        className={`${(window.location.search?.includes(session.sessionId)) ? "selected" : ''} ${(idx === Object.keys(filteredSessions).length - 1) ? "last" : ''}`}
                                        key={session.sessionId}
                                        onClick={isMobile ? undefined : (e) => handleSessionSelection(session, e)}
                                        onTouchStart={isMobile ? handleTouchStart : undefined}
                                        onTouchEnd={isMobile ? (e) => handleTouchEnd(session, e) : undefined}
                                    >
                                        {/* show local time of session, RDS update is now returning server time */}
                                        {moment(session.startDate).format(SESSION_DATE_FORMAT)}
                                        {session?.from?.length ? <SessionPillFrom>{formatPhoneNumber(session.from)}</SessionPillFrom> : null}
                                    </SessionPill>
                                );
                            })
                        }
                    </SessionList>
                </CustomScrollbars>
            </SessionListWrapper>
        )
    };

    const renderMenu = () => {
        return (
            renderSessionList()
        )
    };

    const renderCollapsingPanel = () => {
        return (
            <CollapsingPanel
                isCollapsed={!isLeftExpanded}
                collapsedView={
                    <CollapsedMenuPanel
                        onExpand={(e) => handleToggleExpandLeft(e, true)}
                        expandedViewRender={renderMenu}
                        minimizedDrawer={isMobile}
                    />
                }
                expandedView={
                    <ExpandedMenuPanel
                        onCollapse={(e) => handleToggleExpandLeft(e, false)}
                        expandedViewRender={renderMenu}
                        minimizedDrawer={isMobile}
                    />
                }
            />
        );
    };

    const renderMobileView = () => {
        return (
            <SectionWrapper>
                {renderCollapsingPanel()}
                {renderViewerWrapper()}
                {memoizedModal}
            </SectionWrapper>
        )
    };

    const renderDesktopView = () => {
        return (!isMobile &&
            <SectionWrapper>
                {renderSessionList()}
                {renderViewerWrapper()}
                <HistoryListWrapper>
                    <CustomScrollbars autoHide autoHeight autoHeightMin={(windowSize.windowSize.innerHeight) - 200} autoHeightMax={(windowSize.windowSize.innerHeight) - 200}>
                        {simulatorContainer.contentItemsFromHistory?.length ? <HistoryHeader>{"CONVERSATION HISTORY"}</HistoryHeader> : null}
                        <HistoryList>
                            {simulatorContainer.contentItemsFromHistory.map((c, idx) =>
                                <SimulatorConversationHistoryItem
                                    key={idx}
                                    turn={mapContentHitToConversationTurn(contentHits[idx])}
                                    content={c}
                                    simple={sessionContainer.simpleView}
                                    index={idx}
                                />
                            )}
                        </HistoryList>
                    </CustomScrollbars>
                </HistoryListWrapper>
                {memoizedModal}
            </SectionWrapper>
        )
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        setTouchStartY(e.touches[0].clientY);
        setTouchStartTime(Date.now());
    };

    const handleTouchEnd = (session: SessionListModel, e: React.TouchEvent<HTMLDivElement>) => {
        const touchEndY = e.changedTouches[0].clientY;
        const touchEndTime = Date.now();
        const deltaY = Math.abs(touchEndY - touchStartY);
        const deltaTime = touchEndTime - touchStartTime;

        // if the duratin of the touch was short, it was a tap(click), if not it was a scroll
        const isClick = deltaTime < 300 && deltaY < 10;

        if (isClick) {
            handleSessionSelection(session, e);
        }
    };

    const handleSessionSelection = (session: SessionListModel, e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (window.location.search?.includes(session.sessionId)) return;

        // Mark that this session change is user-initiated
        userSelectedSessionRef.current = true;

        simulatorContainer.startNewSession();
        const newFilter = updateFilterToBeginningAndEndOfDay(props.filter);
        props.history.push({
            search: `?sessionId=${session.sessionId}&startDate=${newFilter.startDate}&endDate=${newFilter.endDate}&interval=${props.interval}`
        });

        if (isMobile) {
            handleToggleExpandLeft(e as any, false);
        }
    };

    return (
        <>
            {(!loaded || sessionContainer.loading || contentContainer.isLoading) ? (
                <LoaderWrapper><Loader /></LoaderWrapper>
            ) : !detailedAnalyticsEnabled ? (
                <>
                    <EmptyConversationText>
                        <h4>Enable detailed analytics</h4>
                        <p>to view sessions</p>
                    </EmptyConversationText>
                    <Loader static />
                </>
            ) : (
                isMobile ? renderMobileView() : renderDesktopView()
            )}
        </>
    );

};

const LoaderWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    .loader-container {
        display: flex;
        align-items: center;
        justify-content: center;
        .spinner > div {
            background: ${color_colors_ocean};
        }
    }
`;

const SessionReviewContainer = styled.div``;

const SessionList = styled.div`
    font-family: Muli;
    font-style: normal;
    font-weight: normal;
`;

const SectionWrapper = styled.div`
    display: flex;
    flex: 1;
`;

const HistoryListWrapper = styled.div`
    height: 100%;
    width: 60%;
    max-width: 432px;
    min-width: 300px;
`;

const HistoryList = styled.div`
    padding: 16px;
`;

const SessionListWrapper = styled.div`
    max-width: 432px;
    min-width: 300px;
`;

const SessionPill = styled.div`
    height: 56px;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    background: white;
    margin: 0 24px 24px 24px;
    padding: 16px;
    position: relative;
    cursor: pointer;
    &.selected {
        background: ${color_shades_lighter};
    }
    &.last {
        margin-bottom: 32px;
    }
`;

const SessionPillFrom = styled.p`
    bottom: 4px;
    left: 16px;
    position: absolute;
    color: ${color_text_light};
    font-size: 12px;
`;

const StyledSearchField = styled(SearchField)`    
    margin: 0 24px 24px 24px;
`;

const ViewerWrapper = styled.div`
    height: calc(100vh - 200px);
    background: ${color_shades_lighter};
    width: 75%;
    ${breakpoint_small} {
        width: 100%;
        height: calc(100vh - 175px);
    }
`;

const SessionsHeader = styled.div`
    margin: 24px 24px 16px 24px;
    display: flex;
    flex-direction: row;
`

const LeftHeader = styled.div`
    margin-right: auto;
`;

const HistoryHeader = styled.div`
    margin: 24px 0px 0px 24px;
`

const EmptyConversationText = styled.div`
    color: ${color_shades_darkest}; 
    position: absolute;
    top: 50%;
    left: 50%;
    margin: 60px 0 0 -95px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`

const HistoryViewContainer = styled.div`
    padding: 16px;
    height: 100%;
`;

const MobileViewToggle = styled.div`
    display: flex;
    background: white;
    border-bottom: 1px solid ${color_shades_dark};
    padding: 8px 16px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
    flex: 1;
    padding: 8px 16px;
    margin: 0 4px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${({ active }) => (active ? color_shades_lighter : 'white')};
    color: ${({ active }) => (active ? color_shades_darkest : color_text_light)};
    font-size: 14px;
    font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${color_shades_lighter};
    }
`;
export default ApplicationSessionAnalytics;